export const isEmptyObject = (obj) => {
    return obj === undefined || Object.keys(obj).length === 0
};

export const getFileExtension = (filename) => {
    return filename.split('.').pop().toLowerCase();
};

export const isValidImageExtension = (filename) => {
    const extension = getFileExtension(filename);
    return ["jpg", "jpeg", "png"].includes(extension);
};

export const isPngExtension = (filename) => {
    const extension = getFileExtension(filename);
    return extension === "png";
};

export const ERROR_TOAST_CONFIG = {
    position: "top-right",
    autoClose: false,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: false,
    progress: undefined,
};

// user.balance llega como STRING (redux/actions/users.js:33/72-73/138-142
// hacen totalBalance - usedBalance y .toString()) — Number() antes de
// formatear. undefined -> "—" (aún no ha llegado el GET /user); 0 -> "0"
// formateado (nunca "—").
export const formatBalance = (balance, language) => {
    if (balance === undefined || balance === null) {
        return '—';
    }
    const numericBalance = Number(balance);
    if (Number.isNaN(numericBalance)) {
        return '—';
    }
    // useGrouping 'always': ICU no agrupa los cuatro dígitos en es (3588), el
    // resto de idiomas sí (3,588) — el mismo saldo se pintaba distinto según
    // el idioma. El artboard agrupa siempre (2.720).
    return new Intl.NumberFormat(language, { useGrouping: 'always' }).format(numericBalance);
};

// Conteo entero (fotos, pedidos, créditos) con el locale de la app y la misma
// agrupación de miles que el saldo. Única fuente: sustituye a los
// integerFormatter locales que cada pantalla se montaba por su cuenta. El '—'
// de un valor ausente lo decide el llamador.
export const formatCount = (value, locale) => {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0, useGrouping: 'always' }).format(value);
};

// Valor de un INPUT de precio: dos decimales con el separador del idioma
// (3,00 en es / 3.00 en en), sin símbolo de moneda y sin miles. No vale el
// formateador de moneda: el campo ya lleva su sufijo "€ / foto".
export const formatPriceInput = (value, locale) => {
    const numeric = typeof value === 'number' ? value : parsePriceInput(value);
    if (!Number.isFinite(numeric)) {
        return '';
    }
    return new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false }).format(numeric);
};

// Lectura numérica de lo tecleado en un input de precio: acepta un separador
// decimal, coma o punto (el usuario teclea el de su idioma). NaN si está vacío
// o si queda algo que no es un número — el llamador decide qué hacer con él.
export const parsePriceInput = (raw) => {
    if (raw === undefined || raw === null) {
        return NaN;
    }
    if (typeof raw === 'number') {
        return raw;
    }
    const normalized = String(raw).trim().replace(',', '.');
    if (normalized === '') {
        return NaN;
    }
    return Number(normalized);
};

// Fecha corta (dd/mm/aaaa en es, mm/dd/aaaa en en) con relleno de ceros y el
// locale de la app, no el del navegador. Vive aquí porque toda la app pinta la
// misma fecha corta: sin fecha o con fecha inválida devuelve '' (lo que ya
// hacían las pantallas que la duplicaban).
export const formatShortDate = (value, locale) => {
    if (value === undefined || value === null || value === '') {
        return '';
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
};

// Porcentaje a partir de la RAZÓN (0.539 -> "53,9 %" en es, "53.9%" en en:
// el separador antes del % lo pone Intl y en es es U+00A0). Un decimal fijo,
// igual en todas las pantallas que muestran porcentajes.
export const formatPercent = (ratio, locale) => {
    return new Intl.NumberFormat(locale, { style: 'percent', minimumFractionDigits: 1 }).format(ratio);
};

// Referencia corta de pedido: el orderId es "<código evento>#<timestamp>" y la
// app enseña solo el timestamp. Aquí porque lo pintan Pagos y Estadísticas.
export const getOrderNumber = (orderId) => {
    if (!orderId) return '-';
    const parts = orderId.split('#');
    return parts.length > 1 ? parts[1] : '-';
};

export const getInitials = (name) => {
    if (!name) {
        return '—';
    }
    return name.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
};

export const hasStripeAccount = (user) => {
    const id = user?.stripeAccountId;
    return Boolean(id) && id !== 'DELETED';
};

export const isStripeRevoked = (user) => {
    return hasStripeAccount(user) && user?.stripeAccountRevoked === true;
};

export const isStripeStandard = (user) => {
    if (!hasStripeAccount(user) || isStripeRevoked(user)) {
        return false;
    }
    return String(user?.stripeAccountType ?? '').toLowerCase() === 'standard';
};

export const isStripeExpressPending = (user) => {
    if (!hasStripeAccount(user) || isStripeRevoked(user)) {
        return false;
    }
    return String(user?.stripeAccountType ?? '').toLowerCase() !== 'standard';
};

// Chip de la cuenta de cobros, único para toda la app. Habla SOLO de la cuenta propia del
// fotógrafo (la Standard): con una Express todavía no hay cuenta de cobros —está de salida y
// su estado lo cuenta el panel de la cuenta antigua—, así que se pinta el neutro «sin cuenta».
export const stripeAccountChip = (user) => {
    if (isStripeStandard(user)) {
        return user?.isStripeAccountActive === true
            ? { tone: 'ok', key: 'account.profile.stripe.ok' }
            : { tone: 'err', key: 'account.profile.stripe.pending' };
    }
    if (isStripeRevoked(user)) {
        return { tone: 'err', key: 'account.profile.stripe.revoked' };
    }
    return { tone: undefined, key: 'account.profile.stripe.none' };
};

export const stripeBannerVariant = (user) => {
    if (!hasStripeAccount(user)) {
        return null;
    }
    if (isStripeRevoked(user)) {
        return 'revoked';
    }
    if (isStripeExpressPending(user)) {
        return 'migration';
    }
    return user?.isStripeAccountActive === false ? 'validation' : null;
};
